<script setup lang="ts">
// One Surveillance trend line: this week's value, the direction against the
// three weeks before it, and — where the measure has one — the reference line
// it is read against (VI 80). Up is drawn in red because for every measure this
// row carries, up means more vector pressure.
import { computed } from "vue";
import LocalizedNumber from "@/components/LocalizedNumber.vue";
import type { HomeSurveillanceTrend } from "@/type/api";

const props = defineProps<{
	label: string;
	trend: HomeSurveillanceTrend;
}>();

const priorAverage = computed(() => {
	const prior = props.trend.prior_weeks;
	if (prior.length === 0) return props.trend.this_week;
	return prior.reduce((sum, n) => sum + n, 0) / prior.length;
});

const delta = computed(() => props.trend.this_week - priorAverage.value);
const rising = computed(() => delta.value >= 0);

const deltaText = computed(
	() => `${delta.value >= 0 ? "+" : ""}${delta.value.toFixed(1)}`,
);

const deltaTitle = computed(
	() =>
		`${deltaText.value} vs the prior 3-week average (${priorAverage.value.toFixed(1)})`,
);

const priorText = computed(() => props.trend.prior_weeks.join(" · "));

const priorTitle = computed(() =>
	props.trend.prior_weeks
		.map(
			(value, i) => `${props.trend.prior_week_labels[i] ?? "prior"}: ${value}`,
		)
		.join("\n"),
);

const aboveReference = computed(
	() =>
		props.trend.reference !== null &&
		props.trend.this_week >= props.trend.reference,
);
</script>

<template>
	<div class="trend-row">
		<div class="d-flex align-items-baseline justify-content-between gap-1">
			<span class="small fw-semibold text-uppercase text-muted">{{
				label
			}}</span>
			<span
				class="small fw-semibold text-nowrap"
				:class="rising ? 'text-danger' : 'text-success'"
				:title="deltaTitle"
			>
				<i
					class="bi"
					:class="rising ? 'bi-arrow-up-short' : 'bi-arrow-down-short'"
					aria-hidden="true"
				></i>
				<span class="visually-hidden">{{ rising ? "up" : "down" }}</span>
				{{ deltaText }}
			</span>
		</div>
		<div class="d-flex align-items-baseline gap-1">
			<span class="fs-5 fw-bold">
				<LocalizedNumber :value="trend.this_week" />
			</span>
			<span class="small text-muted" :title="priorTitle">
				prior {{ priorText }}
			</span>
		</div>
		<div v-if="trend.reference !== null" class="small text-muted">
			reference line <span class="fw-semibold">{{ trend.reference }}</span> ·
			{{ aboveReference ? "above" : "below" }}
		</div>
	</div>
</template>

<style scoped>
.trend-row + .trend-row {
	margin-top: 0.5rem;
	padding-top: 0.5rem;
	border-top: 1px solid #dee2e6;
}
</style>
